// src/pages/portal/PortalContacts.js — "Contacts" (renamed from "Your
// Investors" 2026-09-21, then from "Your Contacts" — submitted names
// aren't necessarily investors; a Strategic Advisory-tagged one is a
// prospective advisor instead). Two
// tables: every contact this partner has contributed to a mandate's
// pipeline, one row per person with each mandate as a chip (same access
// scope as the per-mandate pipeline table on PortalMandateDetail, just
// aggregated with fuller contact fields — see the investor directory note
// in src/controllers/portalController.js), and
// this partner's own Add Names submissions across every mandate
// (contact_submissions, migrations/041) that are pending, or approved but
// not yet in a pipeline (approval sets the status only for now, 10/21).
// A name RGA does not approve stays here, marked Not approved (Travis,
// 10/29).
import { useQuery } from '@tanstack/react-query'
import PortalSearch from '../../components/portal/PortalSearch'
import { Link } from 'react-router-dom'
import api from '../../lib/api'
import { PL_BADGE } from '../../lib/pipelineStages'
import { PortalPeekLink, usePortalDirectory } from '../../components/portal/PortalPeek'
import { nameTypeLabel } from '../../lib/nameTypes'
import { PORTAL_PAGE_TITLE_STYLE, PORTAL_TITLE_ALIGN } from '../../lib/portalPageTitleStyle'

function formatCurrency(n) {
  if (n == null) return '—'
  return `$${Number(n).toLocaleString()}`
}

function formatDate(d) {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

const SUBMITTED_STATUS = {
  pending:  { label: 'Pending approval', badge: 'badge-yellow' },
  approved: { label: 'Approved',         badge: 'badge-green' },
  rejected: { label: 'Not approved',     badge: 'badge-gray' },
}

function SubmittedNamesTable() {
  const { data, isLoading } = useQuery({
    queryKey: ['portalMySubmissions', 'all'],
    queryFn: () => api.get('/api/portal/my-submissions').then(r => r.data.data),
  })
  const submissions = (data ?? []).filter(s =>
    s.status === 'pending' || s.status === 'rejected' || (s.status === 'approved' && !s.promoted_pipeline_id))

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
        <div className="spinner spinner-lg" />
      </div>
    )
  }

  if (!submissions.length) return null

  return (
    <div style={{ marginTop: 32 }}>
      <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12, color: 'var(--text-primary)' }}>Submitted Names</h2>
      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Firm</th>
              <th>Email</th>
              <th>Type</th>
              <th>Mandate</th>
              <th>Submitted</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {submissions.map(s => (
              <tr key={s.submission_id}>
                <td>
                  <div style={{ fontWeight: 600 }}>{s.contact_name}</div>
                  {s.title_role && (
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{s.title_role}</div>
                  )}
                </td>
                <td>{s.firm_name ?? '—'}</td>
                <td>{s.email ?? '—'}</td>
                <td>{nameTypeLabel(s.name_type, true) || '—'}</td>
                <td>
                  <Link to={`/portal/mandates/${s.mandate_id}`}>{s.mandate?.mandate_name ?? '—'}</Link>
                </td>
                <td>{formatDate(s.created_at)}</td>
                <td>
                  <span className={`badge ${SUBMITTED_STATUS[s.status].badge}`}>{SUBMITTED_STATUS[s.status].label}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// One row per person across every mandate (item 8, Matt 10/8: a partner
// sees everyone they have been approved to approach and the activity on
// each). The directory returns one row per pipeline entry; rows for the
// same contact, or the same firm when there is no named contact, are
// merged, each mandate shown as a chip with its stage. Approved Add Names
// submissions arrive here through the pipeline row RGA creates.
function groupByPerson(rows) {
  const byKey = new Map()
  for (const r of rows) {
    const key = r.contact_id ?? `firm:${r.investor_firm_name ?? r.pipeline_id}`
    let p = byKey.get(key)
    if (!p) {
      p = {
        key, name: r.investor_contact_name || r.investor_firm_name || '—',
        contact_id: r.contact_id, firm_id: r.investor_firm_id,
        title: r.title_role, firm: r.investor_contact_name ? r.investor_firm_name : null,
        email: r.email?.[0], phone: r.phone?.[0], last: null, mandates: [],
      }
      byKey.set(key, p)
    }
    p.mandates.push(r)
    if (r.last_activity && (!p.last || r.last_activity > p.last)) p.last = r.last_activity
  }
  return [...byKey.values()].sort((a, b) => (b.last ?? '').localeCompare(a.last ?? '') || a.name.localeCompare(b.name))
}

function MandateChip({ row }) {
  const commit = [
    row.target_commitment != null && `Target ${formatCurrency(row.target_commitment)}`,
    row.actual_commitment != null && `Committed ${formatCurrency(row.actual_commitment)}`,
  ].filter(Boolean).join(' · ')
  return (
    <Link to={`/portal/mandates/${row.mandate_id}`} title={commit || undefined}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none',
        border: '1px solid var(--border)', borderRadius: 6, padding: '3px 6px 3px 8px',
        fontSize: 12, color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>
      {row.mandate_name}
      <span className={`badge ${PL_BADGE[row.stage] ?? 'badge-gray'}`} style={{ fontSize: 10 }}>{row.stage}</span>
    </Link>
  )
}

export default function PortalContacts() {
  const { data: investors, isLoading } = usePortalDirectory()
  const people = groupByPerson(investors ?? [])

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-text" style={{ flex: 1 }}>
          <h1 style={PORTAL_PAGE_TITLE_STYLE}>Contacts</h1>
          <p style={{ textAlign: PORTAL_TITLE_ALIGN }}>
            Your contacts across all your engagements, with each mandate and where it stands. Relationships are protected by mandate.
          </p>
        </div>
        <PortalSearch />
      </div>

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <div className="spinner spinner-lg" />
        </div>
      ) : !people.length ? (
        <div className="empty-state">
          <h3>No contacts yet</h3>
          <p>Names you submit for a mandate show up here once RGA approves them.</p>
        </div>
      ) : (
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Contact</th>
                <th>Firm</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Mandates</th>
                <th>Last Activity</th>
              </tr>
            </thead>
            <tbody>
              {people.map(p => (
                <tr key={p.key}>
                  <td>
                    <div style={{ fontWeight: 600 }}>
                      {p.contact_id
                        ? <PortalPeekLink type="contact" id={p.contact_id}>{p.name}</PortalPeekLink>
                        : <PortalPeekLink type="firm" id={p.firm_id}>{p.name}</PortalPeekLink>}
                    </div>
                    {p.title && <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{p.title}</div>}
                  </td>
                  <td>{p.firm ? <PortalPeekLink type="firm" id={p.firm_id}>{p.firm}</PortalPeekLink> : '—'}</td>
                  <td>{p.email ?? '—'}</td>
                  <td>{p.phone ?? '—'}</td>
                  <td>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {p.mandates.map(r => <MandateChip key={r.pipeline_id} row={r} />)}
                    </div>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>{formatDate(p.last)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SubmittedNamesTable />
    </div>
  )
}
